/**
 * dateFormat — shared ISO-date formatting for the editor's pills.
 *
 * Consolidates the two near-duplicate parsers that lived in
 * MeetingHeaderView.tsx (formatHumanDate, full month names) and
 * ActionItemView.tsx (formatShortDate, abbreviated months). Same
 * input format (`YYYY-MM-DD` from a native date input or the
 * server), same null/empty-string handling, same defensive parse.
 *
 * Not used by the API export pipelines — those have their own
 * formatDate inside clientMeetingTemplate.ts and html.ts that
 * use `Date.toLocaleDateString` with a UTC timezone for
 * deterministic output. The editor side prefers a hand-rolled
 * formatter so we don't depend on the user's locale and so
 * "March 15, 2026" reads identically on every machine.
 */

const MONTHS_LONG = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const MONTHS_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

interface ParsedDate {
  y: number;
  m: number;
  d: number;
}

/** Defensive parse of a `YYYY-MM-DD` string. Returns null if the
 *  shape doesn't match — callers should fall back to the raw
 *  input rather than rendering "NaN" or similar. */
function parseIsoDate(iso: string | undefined | null): ParsedDate | null {
  if (!iso) return null;
  const parts = iso.split('-');
  if (parts.length !== 3) return null;
  const y = Number.parseInt(parts[0], 10);
  const m = Number.parseInt(parts[1], 10);
  const d = Number.parseInt(parts[2], 10);
  if (!y || !m || !d || m < 1 || m > 12 || d < 1 || d > 31) return null;
  return { y, m, d };
}

/** "March 15, 2026" — used in the meeting-header date pills where
 *  there's room for the full month name. */
export function formatHumanDate(iso: string | undefined | null): string {
  const parsed = parseIsoDate(iso);
  if (!parsed) return iso ?? '';
  return `${MONTHS_LONG[parsed.m - 1]} ${parsed.d}, ${parsed.y}`;
}

/** "Mar 15, 2026" — used in the action-item due-date pill where
 *  the chip is small and the full month name would wrap. */
export function formatShortDate(iso: string | undefined | null): string {
  const parsed = parseIsoDate(iso);
  if (!parsed) return iso ?? '';
  return `${MONTHS_SHORT[parsed.m - 1]} ${parsed.d}, ${parsed.y}`;
}
